import { useId, useCallback, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useParams } from "react-router";
import { getDataset } from "../../../api/knowledgeData/knowledgeDataApi";
import type { Dataset } from "../../../api/knowledgeData/knowledgeDataDecoders";
import { getResearchSpaceProject, listReleases } from "../../../api/knowledgeData/releasesApi";
import type { ReleaseSummary, ResearchSpaceProject } from "../../../api/knowledgeData/releaseDecoders";
import { useProjects } from "../../../projects/projectContext";
import { formatTimeAgo } from "../../../shared/formatting/relativeTimes";
import { useLoad } from "../../../shared/loading/useLoad";
import CopyReference from "../../../ui/content/CopyReference";
import DefinitionListItem from "../../../ui/layout/DefinitionListItem";
import LoadStatus from "../../../ui/feedback/LoadStatus";
import { useMemberNames } from "../../threads/data/useMemberNames";
import ResearchSpaceLink from "../ResearchSpaceLink";
import VersionChecks from "../datasets/VersionChecks";
import PublishToResearchSpace from "./PublishToResearchSpace";
import ReleaseDownloads from "./ReleaseDownloads";
import { notifyReleasesChanged } from "./releaseChanges";
import { releaseDecision } from "./releasePermission";
import { useTabTitle } from "../../../shell/workspace";

interface ReleasePageData {
  readonly dataset: Dataset;
  readonly researchSpace: ResearchSpaceProject;
  /** Each version's release, with its state in ResearchSpace, by number. */
  readonly releases: ReadonlyMap<number, ReleaseSummary>;
}

async function loadRelease(datasetId: string, signal: AbortSignal): Promise<ReleasePageData> {
  const dataset = await getDataset(datasetId, signal);
  const [researchSpace, releases] = await Promise.all([
    getResearchSpaceProject(dataset.projectId, signal),
    listReleases(dataset.projectId, signal),
  ]);
  return {
    dataset,
    researchSpace,
    releases: new Map(releases.filter((release) => release.datasetId === datasetId).map((release) => [release.version.number, release])),
  };
}

/**
 * `/data-model/releases/<dataset>`, opened from the Data model's releases list (K-12): a release series with its
 * metadata and immutable versions. Each version downloads as the same bytes every time and
 * shows its state in ResearchSpace; new releases are made with "Release…" in the page's header.
 */
export default function ReleasePage() {
  const { t } = useTranslation();
  const metadataHeadingId = useId();
  const versionsHeadingId = useId();
  const { datasetId = "" } = useParams();
  const { hash } = useLocation();
  const load = useCallback((signal: AbortSignal) => loadRelease(datasetId, signal), [datasetId]);
  const release = useLoad(load);
  useTabTitle(release.state.status === "loaded" ? { kind: "other", name: release.state.value.dataset.metadata.title } : null);
  // In this series' list only: another tab may show another series with the same version numbers (ADR-0039).
  const versions = useRef<HTMLElement>(null);
  // Only a version number the address names: any other fragment text never reaches a selector (Sol V1).
  const addressedVersion = /^#version-([1-9][0-9]*)$/.exec(hash)?.[1];
  useEffect(() => {
    if (release.state.status === "loaded" && addressedVersion !== undefined) {
      versions.current?.querySelector(`[id="version-${addressedVersion}"]`)?.scrollIntoView({ block: "nearest" });
    }
  }, [release.state, addressedVersion]);
  if (release.state.status !== "loaded") return <LoadStatus state={release.state} loadingLabel={t("common.loading")} loadingClassName="py-10" onRetry={release.reload} />;
  const { dataset } = release.state.value;

  return (
    <div className="mx-auto max-w-5xl space-y-4">
      <header className="space-y-1">
        <div className="flex flex-wrap items-center gap-2">
          <h2 className="app-title text-xl font-bold">{dataset.metadata.title}</h2>
          <CopyReference value={dataset.iri} label={t("data.dataset.copyIri")} />
        </div>
      </header>
      <section className="app-card space-y-2 p-3" aria-labelledby={metadataHeadingId}>
        <h3 id={metadataHeadingId} className="app-title text-base font-semibold">{t("data.dataset.metadata")}</h3>
        <dl className="space-y-1 text-sm">
          <DefinitionListItem term={t("data.dataset.fields.description")}>{dataset.metadata.description || "—"}</DefinitionListItem>
          <DefinitionListItem term={t("data.dataset.fields.licence")}>{dataset.metadata.licence}</DefinitionListItem>
        </dl>
        <p className="app-text-muted text-xs">{t("data.release.metadataInDialog")}</p>
      </section>
      <section ref={versions} className="app-card space-y-2 p-3" aria-labelledby={versionsHeadingId}>
        <h3 id={versionsHeadingId} className="app-title text-base font-semibold">{t("data.release.versions")}</h3>
        <p className="app-text-muted text-xs">{t("data.dataset.versionsImmutable")}</p>
        <VersionList data={release.state.value} onPublished={() => { release.refresh(); notifyReleasesChanged(); }} />
      </section>
    </div>
  );
}

function VersionList({ data, onPublished }: { data: ReleasePageData; onPublished: () => void }) {
  const { t, i18n } = useTranslation();
  const { dataset } = data;
  const people = useMemberNames(dataset.projectId);
  const publishing = releaseDecision(useProjects(), dataset.projectId);
  if (dataset.versions.length === 0) {
    return <p className="app-text-muted text-sm">{t("data.dataset.noVersions")}</p>;
  }
  return (
    <ul className="space-y-2">
      {dataset.versions.map((version) => (
        <li id={`version-${String(version.number)}`} key={version.number} className="app-surface space-y-2 rounded border p-2 text-sm">
          <div className="flex flex-wrap items-center gap-2">
            <span className="flex-1">
              <span className="flex items-center gap-1">
                <span className="font-semibold">{t("data.release.number", { number: version.number })}</span>
                <CopyReference value={version.packageDigest} label={t("data.dataset.copyDigest")} />
              </span>
              <span className="app-text-muted block text-xs">
                {t("data.dataset.versionFacts", {
                  count: version.members,
                  name: people.nameOf(version.createdBy),
                  time: formatTimeAgo(version.createdAt, i18n.resolvedLanguage),
                })}
              </span>
            </span>
            <ResearchSpaceLink iri={version.iri} />
          </div>
          <ReleaseDownloads datasetId={dataset.datasetId} version={version} />
          <PublishToResearchSpace
            datasetId={dataset.datasetId}
            version={version}
            researchSpace={data.researchSpace}
            release={data.releases.get(version.number) ?? null}
            publishing={publishing}
            onPublished={onPublished}
          />
          <VersionChecks datasetId={dataset.datasetId} version={version} />
        </li>
      ))}
    </ul>
  );
}
